import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { formatPercent, type DsLocale } from '../format';
import { chartFill, roundedShares, type ChartSeries } from './scale';
import type { ChartLayout } from './layout';

export type SplitBarPart = { id: string; label: ReactNode; value: number; series: ChartSeries };

export type SplitBarProps = {
  /** Two to five parts of one whole. Work model always uses the remote, hybrid and onsite series. */
  parts: SplitBarPart[];
  locale: DsLocale;
  /** One sentence with every share, for screen readers. */
  description: string;
  /** `auto` (default) switches to the narrow form in a narrow container: the labels under the bar stack one per line. */
  layout?: ChartLayout;
  /**
   * Leave out the shares written under the bar when the page writes its own legend next to it (with
   * links or live counts); that legend must then name every part and its share.
   */
  legend?: boolean;
  className?: string;
};

/**
 * One bar split into the parts of a whole, with each part's share written under it in the same
 * order. Shares are whole numbers that add up to 100. Never a pie or a donut instead.
 */
const LEGEND: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-1 @md:flex-row @md:flex-wrap @md:gap-x-4 @md:gap-y-1',
  narrow: 'flex flex-col gap-1',
  wide: 'flex flex-wrap gap-x-4 gap-y-1',
};

export function SplitBar({ parts, locale, layout = 'auto', legend = true, description, className }: SplitBarProps) {
  devWarn(parts.length < 2 || parts.length > 5, `SplitBar shows two to five parts, got ${parts.length}.`);
  const shares = roundedShares(parts.map((p) => p.value));
  const total = parts.reduce((s, p) => s + Math.max(0, p.value), 0) || 1;
  return (
    <div className={cx('@container flex flex-col gap-2', className)}>
      <div
        role="img"
        aria-label={description}
        className="flex h-7 origin-left gap-0.5 overflow-hidden rounded-jc-info motion-safe:animate-jc-grow-x"
      >
        {parts.map((p) => (
          <span
            key={p.id}
            className={chartFill(p.series)}
            style={{ flexGrow: Math.max(0, p.value) / total, flexBasis: 0 }}
          />
        ))}
      </div>
      {legend ? (
        <ul aria-hidden="true" className={cx(LEGEND[layout], 'text-sm font-semibold text-jc-ink-secondary')}>
          {parts.map((p, i) => (
            <li key={p.id} className="flex items-center gap-1.5">
              <span className={cx('size-2.5 rounded-jc-info', chartFill(p.series))} />
              {p.label}
              <span className="font-display font-bold text-jc-ink tabular-nums">
                {formatPercent(shares[i], locale)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
